import { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Typography, keyframes, IconButton, Tooltip } from '@mui/material';

// ─── Keyframe animations ──────────────────────────────────────────────────────
const scaleIn = keyframes`
    0% { transform: scale(0); opacity: 0; }
    60% { transform: scale(1.15); }
    100% { transform: scale(1); opacity: 1; }
`;

const fadeUp = keyframes`
    0% { transform: translateY(20px); opacity: 0; }
    100% { transform: translateY(0); opacity: 1; }
`;

// NOTE: a previous version animated the checkmark via stroke-dashoffset
// inside a parent that itself ran a `scaleIn` keyframe. The combination
// raced and the inner stroke animation occasionally settled at offset=50
// (invisible), leaving an empty green circle on the success screen.
// The tick is now drawn statically — the surrounding circle keeps the
// scaleIn entrance, which is enough motion on its own.
const ringPulse = keyframes`
    0% { transform: scale(0.8); opacity: 0; }
    50% { opacity: 0.35; }
    100% { transform: scale(2.4); opacity: 0; }
`;

const ringPulseSoft = keyframes`
    0% { transform: scale(0.9); opacity: 0; }
    40% { opacity: 0.2; }
    100% { transform: scale(2.8); opacity: 0; }
`;

const float = keyframes`
    0%, 100% { transform: translateY(0) translateX(0); opacity: 0; }
    20% { opacity: 0.6; }
    100% { transform: translateY(-80px) translateX(var(--drift, 0px)); opacity: 0; }
`;

const cardEntry = keyframes`
    0% { transform: translateY(24px) scale(0.985); opacity: 0; }
    100% { transform: translateY(0) scale(1); opacity: 1; }
`;

const shimmer = keyframes`
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
`;

// ─── Section-badge animations (e.g. "What happens next" tick) ────────────────
// Subtle, calm motion — fits UK medical-form tone. badgePop scales the badge
// in with a tiny overshoot; tickDraw animates the checkmark stroke from
// invisible to fully drawn so it reads as a confirmation, not a static glyph.
const badgePop = keyframes`
    0%   { transform: scale(0.4); opacity: 0; }
    60%  { transform: scale(1.08); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
`;

const tickDraw = keyframes`
    0%   { stroke-dashoffset: 28; }
    100% { stroke-dashoffset: 0; }
`;

// ─── Floating ambient particle ───────────────────────────────────────────────
// Subtle drift up around the checkmark; deliberately understated for a
// medical context — no confetti, no garish colour.
const Particle = ({ left, delay, size, drift, color }) => (
    <Box
        sx={{
            position: 'absolute',
            left,
            bottom: 0,
            width: size,
            height: size,
            borderRadius: '50%',
            bgcolor: color,
            opacity: 0,
            animation: `${float} 2.6s ease-out ${delay} forwards`,
            '--drift': `${drift}px`,
            pointerEvents: 'none',
        }}
    />
);

Particle.propTypes = {
    left: PropTypes.string.isRequired,
    delay: PropTypes.string.isRequired,
    size: PropTypes.number.isRequired,
    drift: PropTypes.number.isRequired,
    color: PropTypes.string.isRequired,
};

// ─── "What happens next" row with inline icon ────────────────────────────────
const NextRow = ({ icon, children, delay }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.25,
            mb: 1,
            '&:last-of-type': { mb: 0 },
            animation: `${fadeUp} 0.4s ease-out ${delay} both`,
        }}
    >
        <Box
            sx={{
                width: 22,
                height: 22,
                borderRadius: '6px',
                bgcolor: 'rgba(0, 171, 85, 0.12)',
                color: '#00AB55',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                mt: '1px',
            }}
        >
            {icon}
        </Box>
        <Typography sx={{ color: '#54677A', fontSize: '13px', lineHeight: 1.6, flex: 1 }}>
            {children}
        </Typography>
    </Box>
);

NextRow.propTypes = {
    icon: PropTypes.node.isRequired,
    children: PropTypes.node.isRequired,
    delay: PropTypes.string.isRequired,
};

const ConsentSubmitSuccess = ({
    patientName,
    pdfDownloadUrl,
    pdfFileName,
    canResubmit,
    submissionCount,
    maxSubmissions,
    onEditResubmit,
    patientEmail,
    sequenceInfo,
    onNextForm,
    formName,
    practiceName,
    submittedAt,
}) => {
    const [showContent, setShowContent] = useState(false);
    const [emailCopied, setEmailCopied] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setShowContent(true), 400);
        return () => clearTimeout(timer);
    }, []);

    // ─── Reset scroll on mount ───────────────────────────────────────────
    // The form submit handler unmounts the renderer and mounts this success
    // screen, but the browser keeps the previous scroll position — so the
    // patient lands at the bottom of the success card (looking at the
    // "Spotted a mistake?" link) and misses the green tick + thank-you.
    // Force scroll to top synchronously in a layout effect, then re-run on
    // the next frame to defeat any focus-restoration scroll the browser
    // performs after the re-render.
    useLayoutEffect(() => {
        const resetScroll = () => {
            window.scrollTo(0, 0);
            if (document.documentElement) document.documentElement.scrollTop = 0;
            if (document.body) document.body.scrollTop = 0;
        };
        resetScroll();
        const raf = requestAnimationFrame(resetScroll);
        return () => cancelAnimationFrame(raf);
    }, []);

    // ─── Format submission timestamp ─────────────────────────────────────
    // Patients trust short, locale-aware timestamps over ISO strings.
    const submittedAtLabel = (() => {
        const d = submittedAt ? new Date(submittedAt) : new Date();
        if (Number.isNaN(d.getTime())) return null;
        try {
            return d.toLocaleString(undefined, {
                hour: 'numeric',
                minute: '2-digit',
                day: 'numeric',
                month: 'short',
                year: 'numeric',
            });
        } catch {
            return d.toISOString();
        }
    })();

    const handleDownloadPdf = () => {
        if (!pdfDownloadUrl) return;
        const link = document.createElement('a');
        link.href = pdfDownloadUrl;
        link.download = pdfFileName || 'consent-form-signed.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleCopyEmail = async () => {
        if (!patientEmail) return;
        try {
            await navigator.clipboard.writeText(patientEmail);
            setEmailCopied(true);
            setTimeout(() => setEmailCopied(false), 1800);
        } catch {
            // Clipboard API unsupported / blocked — fail silently;
            // the email is already visible on screen so nothing is lost.
        }
    };

    const hasMoreForms = sequenceInfo && sequenceInfo.current < sequenceInfo.total;

    // Sequence-end "Download all" — only meaningful on the LAST form in
    // a multi-form sequence. We surface every sibling submitted by the
    // patient with its own download link, so they can keep a record of
    // everything they just signed in one place instead of having to
    // dig back through emails. The current form's blob (``pdfDownloadUrl``)
    // is preferred over the BE-rendered ``pdf_url`` for the current row
    // because it reflects the high-fidelity browser render that just
    // succeeded; siblings fall back to the BE URL supplied via
    // ``sequenceInfo.forms[i].pdf_url`` (forms_public_views.py
    // _get_sequence_info).
    const siblingForms = Array.isArray(sequenceInfo?.forms) ? sequenceInfo.forms : [];
    const isLastInSequence = sequenceInfo
        && sequenceInfo.total > 1
        && sequenceInfo.current === sequenceInfo.total;

    const handleOpenSiblingPdf = (url) => {
        if (!url) return;
        // Open in a new tab rather than forcing a download because the
        // sibling PDF lives on the BE storage backend and the patient
        // might want to preview before saving. Browsers honour
        // Content-Disposition headers set by the storage layer for the
        // direct-save case.
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    // Memoize particles so re-renders don't reshuffle their drift values
    // mid-animation (which would otherwise visibly hitch the float).
    const particles = useMemo(
        () => [
            { left: '20%', delay: '0.5s', size: 4, drift: -10, color: 'rgba(0, 171, 85, 0.45)' },
            { left: '35%', delay: '0.7s', size: 5, drift: 12, color: 'rgba(0, 171, 85, 0.35)' },
            { left: '55%', delay: '0.6s', size: 3, drift: -6, color: 'rgba(0, 171, 85, 0.5)' },
            { left: '70%', delay: '0.9s', size: 5, drift: 14, color: 'rgba(0, 171, 85, 0.3)' },
            { left: '85%', delay: '0.8s', size: 4, drift: -12, color: 'rgba(0, 171, 85, 0.4)' },
        ],
        [],
    );

    return (
        <Box sx={{
            maxWidth: 720,
            mx: 'auto',
            py: { xs: 6, sm: 10 },
            px: { xs: 2, sm: 3 },
            textAlign: 'center',
        }}>
            <Box sx={{
                bgcolor: '#fff',
                borderRadius: '24px',
                p: { xs: 4, sm: 6 },
                boxShadow: '0 0 2px rgba(145,158,171,0.2), 0 16px 32px -8px rgba(145,158,171,0.18)',
                overflow: 'hidden',
                position: 'relative',
                animation: `${cardEntry} 0.6s cubic-bezier(0.22, 1, 0.36, 1) both`,
                // Soft top edge tint — picks up the success colour without
                // being loud about it.
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, transparent, #00AB55, #5BE584, #00AB55, transparent)',
                    backgroundSize: '200% 100%',
                    animation: `${shimmer} 3s ease-in-out 0.4s 1`,
                },
            }}>
                {/* Hero — animated checkmark + ambient particles */}
                <Box sx={{
                    position: 'relative',
                    width: 120,
                    height: 120,
                    mx: 'auto',
                    mb: 3,
                }}>
                    {/* Ambient particles drifting upward */}
                    {particles.map((p) => (
                        <Particle key={p.left} {...p} />
                    ))}

                    {/* Outer pulse ring (slower, softer) */}
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        width: 88,
                        height: 88,
                        transform: 'translate(-50%, -50%)',
                        borderRadius: '50%',
                        border: '2px solid #00AB55',
                        animation: `${ringPulseSoft} 2s ease-out 0.5s`,
                        animationFillMode: 'forwards',
                        opacity: 0,
                    }} />

                    {/* Inner pulse ring */}
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        width: 88,
                        height: 88,
                        transform: 'translate(-50%, -50%)',
                        borderRadius: '50%',
                        border: '3px solid #00AB55',
                        animation: `${ringPulse} 1.5s ease-out 0.3s`,
                        animationFillMode: 'forwards',
                        opacity: 0,
                    }} />

                    {/* Green circle with check */}
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 88,
                        height: 88,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(0, 171, 85, 0.14) 0%, rgba(91, 229, 132, 0.18) 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 8px 24px -8px rgba(0, 171, 85, 0.4)',
                        animation: `${scaleIn} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards`,
                    }}>
                        <svg width="44" height="44" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="11" stroke="#00AB55" strokeWidth="2" fill="rgba(0, 171, 85, 0.12)" />
                            <path
                                d="M7.5 12.5L10.5 15.5L16.5 9"
                                stroke="#00AB55"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                fill="none"
                            />
                        </svg>
                    </Box>
                </Box>

                {/* Text content — fades up after checkmark */}
                <Box sx={{
                    opacity: showContent ? 1 : 0,
                    transform: showContent ? 'translateY(0)' : 'translateY(16px)',
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, color: '#212B36', letterSpacing: '-0.01em' }}>
                        {formName ? `${formName} — Submitted` : 'Form Submitted'}
                    </Typography>

                    {submittedAtLabel && (
                        <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.625,
                            mb: 2.25,
                            px: 1.25,
                            py: 0.375,
                            borderRadius: '999px',
                            bgcolor: 'rgba(0, 171, 85, 0.08)',
                        }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#0F6E56" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <polyline points="12 6 12 12 16 14" />
                            </svg>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: '#0F6E56',
                                    fontSize: '11.5px',
                                    fontWeight: 600,
                                    letterSpacing: '0.01em',
                                }}
                            >
                                Submitted {submittedAtLabel}
                            </Typography>
                        </Box>
                    )}

                    <Typography variant="body1" sx={{ color: '#637381', mb: 3, lineHeight: 1.6 }}>
                        Thank you{patientName ? `, ${patientName}` : ''}. Your form has been received
                        {practiceName ? ` by ${practiceName}` : ''} and securely stored.
                    </Typography>

                    {/* PDF — download if available, otherwise async email notice */}
                    {pdfDownloadUrl ? (
                        <Button
                            variant="contained"
                            onClick={handleDownloadPdf}
                            disableElevation
                            startIcon={
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                            }
                            sx={{
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 3.5,
                                py: 1.25,
                                mb: 2,
                                bgcolor: '#00AB55',
                                color: '#fff',
                                boxShadow: '0 8px 16px -4px rgba(0, 171, 85, 0.35)',
                                animation: `${fadeUp} 0.4s ease-out 0.8s both`,
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    bgcolor: '#007B40',
                                    boxShadow: '0 12px 20px -4px rgba(0, 171, 85, 0.45)',
                                    transform: 'translateY(-1px)',
                                },
                                '&:active': {
                                    transform: 'translateY(0)',
                                },
                            }}
                        >
                            Download Your Copy (PDF)
                        </Button>
                    ) : (
                        <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 2,
                            px: 2,
                            py: 1.25,
                            borderRadius: '10px',
                            bgcolor: 'rgba(7, 141, 238, 0.06)',
                            animation: `${fadeUp} 0.4s ease-out 0.8s both`,
                        }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#078DEE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="2" y="4" width="20" height="16" rx="2" />
                                <path d="M22 7l-10 7L2 7" />
                            </svg>
                            <Typography variant="body2" sx={{ color: '#637381', fontSize: '12px' }}>
                                A PDF copy will be emailed to {patientEmail || 'you'} shortly
                            </Typography>
                        </Box>
                    )}

                    {/* Sequence end — all forms in this batch are
                        signed. Surface a download link for each sibling
                        the patient just signed so they leave with the
                        full set, not just the current row's PDF. The
                        current form is rendered separately from the
                        client-side blob above; this list intentionally
                        excludes the current row to avoid a duplicate
                        download button. Hidden for single-form sends
                        (sequenceInfo.total === 1) and for mid-sequence
                        success screens (hasMoreForms === true). */}
                    {isLastInSequence && siblingForms.length > 1 && (() => {
                        const currentToken = siblingForms.find(
                            (f, idx) => (f.sequence_order ?? idx) + 1 === sequenceInfo.current,
                        )?.access_token;
                        const otherSiblings = siblingForms.filter(
                            (f) => f.access_token !== currentToken
                                && f.pdf_url
                                && f.status === 'SUBMITTED',
                        );
                        if (otherSiblings.length === 0) return null;
                        return (
                            <Box
                                sx={{
                                    mt: 1.5,
                                    mb: 2,
                                    px: 2.25,
                                    py: 2,
                                    borderRadius: '14px',
                                    background: 'linear-gradient(135deg, rgba(0, 171, 85, 0.04) 0%, rgba(91, 229, 132, 0.06) 100%)',
                                    border: '1px solid rgba(0, 171, 85, 0.16)',
                                    textAlign: 'left',
                                    animation: `${fadeUp} 0.4s ease-out 0.85s both`,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
                                    <Box sx={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: '8px',
                                        bgcolor: '#00AB55',
                                        color: '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        boxShadow: '0 2px 6px -1px rgba(0,171,85,0.45)',
                                        animation: `${badgePop} 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) 0.95s both`,
                                        transformOrigin: 'center',
                                    }}>
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                            <polyline points="14 2 14 8 20 8" />
                                        </svg>
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: '12px',
                                            fontWeight: 700,
                                            color: '#0F6E56',
                                            letterSpacing: '0.04em',
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        Other forms in this batch
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {otherSiblings.map((sibling, idx) => (
                                        <Box
                                            key={sibling.access_token}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                py: 0.875,
                                                px: 1.25,
                                                borderRadius: '8px',
                                                bgcolor: 'rgba(255, 255, 255, 0.6)',
                                                border: '1px solid rgba(0, 171, 85, 0.08)',
                                                transition: 'all 0.18s ease',
                                                animation: `${fadeUp} 0.35s ease-out ${0.95 + idx * 0.06}s both`,
                                                '&:hover': {
                                                    bgcolor: '#fff',
                                                    borderColor: 'rgba(0, 171, 85, 0.24)',
                                                    transform: 'translateX(2px)',
                                                },
                                            }}
                                        >
                                            <Box sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                bgcolor: '#00AB55',
                                                flexShrink: 0,
                                            }} />
                                            <Box
                                                sx={{
                                                    flex: 1,
                                                    minWidth: 0,
                                                    fontSize: '13px',
                                                    fontWeight: 500,
                                                    color: '#212B36',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                                title={sibling.name}
                                            >
                                                {sibling.name || 'Form'}
                                            </Box>
                                            <Button
                                                size="small"
                                                variant="text"
                                                onClick={() => handleOpenSiblingPdf(sibling.pdf_url)}
                                                startIcon={
                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                        <polyline points="7 10 12 15 17 10" />
                                                        <line x1="12" y1="15" x2="12" y2="3" />
                                                    </svg>
                                                }
                                                sx={{
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    fontSize: '12px',
                                                    color: '#00AB55',
                                                    minWidth: 0,
                                                    px: 1.25,
                                                    py: 0.5,
                                                    borderRadius: '6px',
                                                    '&:hover': { bgcolor: 'rgba(0, 171, 85, 0.08)' },
                                                }}
                                            >
                                                Download
                                            </Button>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        );
                    })()}

                    {/* Sequence — continue to next form */}
                    {hasMoreForms && onNextForm && (
                        <Button
                            variant="contained"
                            onClick={onNextForm}
                            disableElevation
                            endIcon={
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                    <polyline points="12 5 19 12 12 19" />
                                </svg>
                            }
                            sx={{
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 700,
                                px: 4,
                                py: 1.375,
                                mb: 2,
                                bgcolor: '#00AB55',
                                boxShadow: '0 8px 16px -4px rgba(0, 171, 85, 0.35)',
                                animation: `${fadeUp} 0.4s ease-out 0.9s both`,
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    bgcolor: '#007B40',
                                    boxShadow: '0 12px 20px -4px rgba(0, 171, 85, 0.45)',
                                    transform: 'translateY(-1px)',
                                },
                            }}
                        >
                            Continue to next form ({sequenceInfo.current} of {sequenceInfo.total})
                        </Button>
                    )}

                    {/* What happens next — answers the #1 patient question and
                        adds reassurance before the page is closed. */}
                    {!hasMoreForms && (
                        <Box
                            sx={{
                                mt: 2,
                                mb: 2,
                                px: 2.5,
                                py: 2.25,
                                borderRadius: '14px',
                                background: 'linear-gradient(135deg, rgba(0, 171, 85, 0.04) 0%, rgba(91, 229, 132, 0.06) 100%)',
                                border: '1px solid rgba(0, 171, 85, 0.16)',
                                textAlign: 'left',
                                animation: `${fadeUp} 0.4s ease-out 0.95s both`,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.75 }}>
                                <Box sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '8px',
                                    bgcolor: '#00AB55',
                                    color: '#fff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    boxShadow: '0 2px 6px -1px rgba(0,171,85,0.45)',
                                    // Pop-in on entry — runs once after the
                                    // panel fades up (the parent's `fadeUp`
                                    // starts at 0.95s, so we sit just behind
                                    // it for a staggered reveal).
                                    animation: `${badgePop} 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) 1.05s both`,
                                    transformOrigin: 'center',
                                }}>
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.75"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        aria-hidden
                                    >
                                        {/* Tick centred in the viewBox so the
                                            badge reads balanced. stroke-
                                            dasharray = path length (~28) so
                                            the keyframe can draw it from
                                            empty to filled. */}
                                        <polyline
                                            points="5 12.5 10 17.5 19 7.5"
                                            style={{
                                                strokeDasharray: 28,
                                                strokeDashoffset: 28,
                                                animation: `${tickDraw} 0.45s cubic-bezier(0.65, 0, 0.35, 1) 1.3s forwards`,
                                            }}
                                        />
                                    </svg>
                                </Box>
                                <Typography
                                    sx={{
                                        fontSize: '12px',
                                        fontWeight: 700,
                                        color: '#0F6E56',
                                        letterSpacing: '0.04em',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    What happens next
                                </Typography>
                            </Box>

                            <NextRow
                                delay="1.05s"
                                icon={(
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                        <circle cx="12" cy="7" r="4" />
                                    </svg>
                                )}
                            >
                                Your {practiceName ? `${practiceName} ` : ''}clinical team has been
                                notified and will review your responses.
                            </NextRow>

                            {patientEmail && (
                                <NextRow
                                    delay="1.15s"
                                    icon={(
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                            <polyline points="22,6 12,13 2,6" />
                                        </svg>
                                    )}
                                >
                                    A confirmation copy has been emailed to{' '}
                                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 600, color: '#212B36' }}>
                                        {patientEmail}
                                        <Tooltip title={emailCopied ? 'Copied!' : 'Copy email'} arrow placement="top">
                                            <IconButton
                                                size="small"
                                                onClick={handleCopyEmail}
                                                sx={{
                                                    p: 0.25,
                                                    color: emailCopied ? '#00AB55' : '#919EAB',
                                                    transition: 'color 0.18s ease',
                                                    '&:hover': { color: '#00AB55', bgcolor: 'rgba(0, 171, 85, 0.08)' },
                                                }}
                                            >
                                                {emailCopied ? (
                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="20 6 9 17 4 12" />
                                                    </svg>
                                                ) : (
                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                                    </svg>
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </NextRow>
                            )}

                            <NextRow
                                delay="1.25s"
                                icon={(
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="12" y1="8" x2="12" y2="12" />
                                        <line x1="12" y1="16" x2="12.01" y2="16" />
                                    </svg>
                                )}
                            >
                                If anything looks wrong, contact the practice directly — they can
                                request a fresh form for you.
                            </NextRow>
                        </Box>
                    )}

                    {/* Edit & Resubmit — demoted to a tertiary text link.
                        It's a destructive-ish action (creates a new version
                        of a signed record) and shouldn't compete visually
                        with the primary "Download" CTA. */}
                    {canResubmit && onEditResubmit && (
                        <Box sx={{ mt: 1, mb: 1.5, animation: `${fadeUp} 0.4s ease-out 1.35s both` }}>
                            <Button
                                variant="text"
                                onClick={onEditResubmit}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: '13px',
                                    color: '#637381',
                                    textDecoration: 'underline',
                                    p: 0,
                                    minWidth: 0,
                                    transition: 'color 0.18s ease',
                                    '&:hover': {
                                        bgcolor: 'transparent',
                                        color: '#212B36',
                                    },
                                }}
                            >
                                Spotted a mistake? Edit and resubmit
                            </Button>
                            <Typography variant="caption" sx={{ display: 'block', color: '#919EAB', mt: 0.5, fontSize: '11px' }}>
                                {maxSubmissions - submissionCount} {maxSubmissions - submissionCount === 1 ? 'edit' : 'edits'} remaining
                            </Typography>
                        </Box>
                    )}

                    <Typography variant="body2" sx={{ color: '#919EAB', fontSize: '0.8125rem', mt: 1, animation: `${fadeUp} 0.4s ease-out 1.45s both` }}>
                        {hasMoreForms
                            ? 'You can close this page and continue the remaining forms later.'
                            : 'You’re all set — you can safely close this page.'}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

ConsentSubmitSuccess.propTypes = {
    patientName: PropTypes.string,
    pdfDownloadUrl: PropTypes.string,
    pdfFileName: PropTypes.string,
    canResubmit: PropTypes.bool,
    submissionCount: PropTypes.number,
    maxSubmissions: PropTypes.number,
    onEditResubmit: PropTypes.func,
    patientEmail: PropTypes.string,
    sequenceInfo: PropTypes.shape({
        current: PropTypes.number,
        total: PropTypes.number,
        // Per-sibling list — populated by ConsentFormPublic from the BE
        // _get_sequence_info payload. Empty array on older BE deploys;
        // the "Other forms in this batch" section is suppressed when
        // empty so the FE degrades to single-form download behaviour.
        forms: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string,
                status: PropTypes.string,
                access_token: PropTypes.string,
                pdf_url: PropTypes.string,
                submitted_at: PropTypes.string,
                sequence_order: PropTypes.number,
            }),
        ),
    }),
    onNextForm: PropTypes.func,
    formName: PropTypes.string,
    practiceName: PropTypes.string,
    submittedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
};

ConsentSubmitSuccess.defaultProps = {
    patientName: '',
    pdfDownloadUrl: null,
    pdfFileName: 'consent-form-signed.pdf',
    canResubmit: false,
    submissionCount: 0,
    maxSubmissions: 2,
    onEditResubmit: null,
    patientEmail: '',
    sequenceInfo: null,
    onNextForm: null,
    formName: '',
    practiceName: '',
    submittedAt: null,
};

export default ConsentSubmitSuccess;
